import {
  SNAKES_LADDERS_ROLL_TIME_SECONDS,
  type SnakesLaddersState,
  snakesLaddersTransitions,
} from "@topzle/game-snakes-ladders";
import { Gauge, LoaderCircle } from "lucide-react";
import type { CSSProperties, PointerEvent, ReactNode } from "react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  calculateLudoThrowPhysics,
  clampLudoPhysics,
  constrainLudoThrowToBoard,
  defaultLudoThrowPhysics,
  hasLudoDiceTouchedEdge,
  type LudoThrowPhysics,
} from "../ludoDicePhysics";

interface SnakesLaddersBoardProps {
  state: SnakesLaddersState;
  participantId: string;
  participants: Array<{ id: string; displayName: string; avatar: string }>;
  pending: boolean;
  isHost: boolean;
  settingsPending: boolean;
  serverError: string | null;
  playAgain?: ReactNode;
  onRoll: () => boolean;
  onFastModeChange: (enabled: boolean) => boolean;
  onMovePresentationComplete?: (moveNonce: number) => void;
}

const playerColors = ["blue", "coral", "yellow", "cyan"] as const;
const playerColorHex = {
  blue: "#2f8cff",
  coral: "#ff647c",
  yellow: "#ffc247",
  cyan: "#2ccaf5",
} as const;
const NORMAL_DICE_RESULT_HOLD_MS = 2_200;
const FAST_DICE_RESULT_HOLD_MS = 850;
const NORMAL_TOKEN_STEP_MS = 280;
const FAST_TOKEN_STEP_MS = 105;
const SNAKE_TONGUE_ACTION_OFFSET = 6.55;
const snakePalettes = [
  {
    primary: "#168b58",
    light: "#78d99d",
    dark: "#063f2d",
    belly: "#c8dda1",
    pattern: "#092f25",
    eye: "#e7c650",
  },
  {
    primary: "#b94351",
    light: "#ee8790",
    dark: "#5b1c29",
    belly: "#e8c0a6",
    pattern: "#3e1720",
    eye: "#e6bd4f",
  },
  {
    primary: "#286fa9",
    light: "#74b9dc",
    dark: "#102f54",
    belly: "#b6d4d9",
    pattern: "#071d38",
    eye: "#dfb84e",
  },
  {
    primary: "#a86418",
    light: "#e8b85b",
    dark: "#4e2c08",
    belly: "#e7d5a0",
    pattern: "#392006",
    eye: "#f0cc5a",
  },
] as const;
const dicePips: Record<number, Array<{ left: number; top: number }>> = {
  1: [{ left: 50, top: 50 }],
  2: [
    { left: 27, top: 27 },
    { left: 73, top: 73 },
  ],
  3: [
    { left: 27, top: 27 },
    { left: 50, top: 50 },
    { left: 73, top: 73 },
  ],
  4: [
    { left: 27, top: 27 },
    { left: 73, top: 27 },
    { left: 27, top: 73 },
    { left: 73, top: 73 },
  ],
  5: [
    { left: 27, top: 27 },
    { left: 73, top: 27 },
    { left: 50, top: 50 },
    { left: 27, top: 73 },
    { left: 73, top: 73 },
  ],
  6: [
    { left: 27, top: 23 },
    { left: 73, top: 23 },
    { left: 27, top: 50 },
    { left: 73, top: 50 },
    { left: 27, top: 77 },
    { left: 73, top: 77 },
  ],
};

function squarePoint(square: number) {
  const safeSquare = Math.max(1, Math.min(100, square));
  const rowFromBottom = Math.floor((safeSquare - 1) / 10);
  const offset = (safeSquare - 1) % 10;
  const column = rowFromBottom % 2 === 0 ? offset : 9 - offset;
  return { x: column * 10 + 5, y: (9 - rowFromBottom) * 10 + 5 };
}

function squaresInVisualOrder() {
  return Array.from({ length: 100 }, (_, index) => {
    const visualRow = Math.floor(index / 10);
    const rowFromBottom = 9 - visualRow;
    const visualColumn = index % 10;
    return rowFromBottom * 10 + (rowFromBottom % 2 === 0 ? visualColumn + 1 : 10 - visualColumn);
  });
}

const boardSquares = squaresInVisualOrder();

function Ladder({ from, to }: { from: number; to: number }) {
  const start = squarePoint(from);
  const end = squarePoint(to);
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  return (
    <g className="snl-ladder" transform={`translate(${start.x} ${start.y}) rotate(${angle})`}>
      <line className="snl-ladder-shadow" x1="0" y1="-1.35" x2={length} y2="-1.35" />
      <line className="snl-ladder-shadow" x1="0" y1="1.35" x2={length} y2="1.35" />
      <line x1="0" y1="-1.1" x2={length} y2="-1.1" />
      <line x1="0" y1="1.1" x2={length} y2="1.1" />
      {Array.from({ length: Math.max(3, Math.floor(length / 4)) }, (_, index) => {
        const x = ((index + 0.5) / Math.max(3, Math.floor(length / 4))) * length;
        return <line className="snl-ladder-rung" x1={x} y1="-1.6" x2={x} y2="1.6" key={x} />;
      })}
    </g>
  );
}

// The expressive face and alternating scale treatment are adapted to board geometry from
// Ebaychatter0's CC0 “Cartoon snake.svg”; every body path remains tied to its real game squares.
function Snake({
  from,
  to,
  index,
  biting,
}: {
  from: number;
  to: number;
  index: number;
  biting: boolean;
}) {
  const actionPoint = squarePoint(from);
  const tail = squarePoint(to);
  const dx = tail.x - actionPoint.x;
  const dy = tail.y - actionPoint.y;
  const distance = Math.max(1, Math.hypot(dx, dy));
  const bodyDirectionX = dx / distance;
  const bodyDirectionY = dy / distance;
  const forwardX = -bodyDirectionX;
  const forwardY = -bodyDirectionY;
  const headBase = {
    x: actionPoint.x - forwardX * SNAKE_TONGUE_ACTION_OFFSET,
    y: actionPoint.y - forwardY * SNAKE_TONGUE_ACTION_OFFSET,
  };
  const neckJoin = {
    x: headBase.x + bodyDirectionX * 2.15,
    y: headBase.y + bodyDirectionY * 2.15,
  };
  const bodyDx = tail.x - neckJoin.x;
  const bodyDy = tail.y - neckJoin.y;
  const bodyDistance = Math.max(1, Math.hypot(bodyDx, bodyDy));
  const bodyNormalX = -bodyDirectionY;
  const bodyNormalY = bodyDirectionX;
  const waveDirection = index % 2 === 0 ? 1 : -1;
  const waveAmplitude = Math.min(6.4, Math.max(1.8, bodyDistance * 0.13));
  const makeBodyPath = (firstWave: number, secondWave: number) => {
    const waveOne = {
      x: neckJoin.x + bodyDx * 0.34 + bodyNormalX * waveAmplitude * firstWave,
      y: neckJoin.y + bodyDy * 0.34 + bodyNormalY * waveAmplitude * firstWave,
    };
    const waveTwo = {
      x: neckJoin.x + bodyDx * 0.69 + bodyNormalX * waveAmplitude * secondWave,
      y: neckJoin.y + bodyDy * 0.69 + bodyNormalY * waveAmplitude * secondWave,
    };
    const neckLength = Math.min(6.4, bodyDistance * 0.2);
    const controlLength = Math.min(7.4, bodyDistance * 0.14);
    const tailLength = Math.min(5.8, bodyDistance * 0.16);
    return [
      `M ${neckJoin.x} ${neckJoin.y}`,
      `C ${neckJoin.x + bodyDirectionX * neckLength} ${neckJoin.y + bodyDirectionY * neckLength},`,
      `${waveOne.x - bodyDirectionX * controlLength} ${waveOne.y - bodyDirectionY * controlLength},`,
      `${waveOne.x} ${waveOne.y}`,
      `C ${waveOne.x + bodyDirectionX * controlLength} ${waveOne.y + bodyDirectionY * controlLength},`,
      `${waveTwo.x - bodyDirectionX * controlLength} ${waveTwo.y - bodyDirectionY * controlLength},`,
      `${waveTwo.x} ${waveTwo.y}`,
      `C ${waveTwo.x + bodyDirectionX * controlLength} ${waveTwo.y + bodyDirectionY * controlLength},`,
      `${tail.x - bodyDirectionX * tailLength} ${tail.y - bodyDirectionY * tailLength},`,
      `${tail.x} ${tail.y}`,
    ].join(" ");
  };
  const path = makeBodyPath(waveDirection, -waveDirection * 0.82);
  const movingPath = makeBodyPath(waveDirection * 0.72, -waveDirection * 1.08);
  const waveValues = `${path};${movingPath};${path}`;
  const waveBegin = `${-((index % 5) * 0.37)}s`;
  const bodyWave = () => (
    <animate
      className="snl-snake-wave-motion"
      attributeName="d"
      values={waveValues}
      dur="3.8s"
      begin={waveBegin}
      keyTimes="0;0.5;1"
      keySplines="0.45 0 0.55 1;0.45 0 0.55 1"
      calcMode="spline"
      repeatCount="indefinite"
    />
  );
  const headAngle = (Math.atan2(forwardY, forwardX) * 180) / Math.PI;
  const palette = snakePalettes[index % snakePalettes.length] ?? snakePalettes[0];
  const instanceId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const gradientId = `snl-snake-gradient-${instanceId}`;
  const headGradientId = `snl-snake-head-gradient-${instanceId}`;
  const style = {
    "--snake-primary": palette.primary,
    "--snake-light": palette.light,
    "--snake-dark": palette.dark,
    "--snake-belly": palette.belly,
    "--snake-pattern": palette.pattern,
    "--snake-eye": palette.eye,
    "--snake-delay": `${(index % 5) * -0.37}s`,
  } as CSSProperties;

  return (
    <g className={`snl-snake variant-${index % 4} ${biting ? "biting" : ""}`} style={style}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={palette.light} />
          <stop offset="0.42" stopColor={palette.primary} />
          <stop offset="1" stopColor={palette.dark} />
        </linearGradient>
        <linearGradient
          id={headGradientId}
          gradientUnits="userSpaceOnUse"
          x1="-2.8"
          y1="0"
          x2="5.05"
          y2="0"
        >
          <stop offset="0" stopColor={palette.primary} stopOpacity="0" />
          <stop offset="0.18" stopColor={palette.primary} stopOpacity="0.72" />
          <stop offset="0.38" stopColor={palette.light} />
          <stop offset="0.7" stopColor={palette.primary} />
          <stop offset="1" stopColor={palette.dark} />
        </linearGradient>
      </defs>
      <path className="snl-snake-shadow" d={path} pathLength="100">
        {bodyWave()}
      </path>
      <path className="snl-snake-body" d={path} pathLength="100" stroke={`url(#${gradientId})`}>
        {bodyWave()}
      </path>
      <path className="snl-snake-dorsal-pattern" d={path} pathLength="100">
        {bodyWave()}
      </path>
      <path className="snl-snake-belly" d={path} pathLength="100">
        {bodyWave()}
      </path>
      <path className="snl-snake-scales" d={path} pathLength="100">
        {bodyWave()}
      </path>
      <circle className="snl-snake-tail" cx={tail.x} cy={tail.y} r="0.95" />
      <g
        className="snl-snake-head-group"
        transform={`translate(${headBase.x} ${headBase.y}) rotate(${headAngle})`}
      >
        <path
          className="snl-snake-head-shape"
          fill={`url(#${headGradientId})`}
          d="M -2.8 -2.3 C -1.65 -2.48 -1.05 -2.58 -0.62 -2.72 C 0.2 -3.42 1.48 -3.52 2.72 -2.68 C 3.92 -1.9 4.78 -1.02 5.02 0 C 4.78 1.02 3.92 1.9 2.72 2.68 C 1.48 3.52 0.2 3.42 -0.62 2.72 C -1.05 2.58 -1.65 2.48 -2.8 2.3 Z"
        />
        <path
          className="snl-snake-head-outline"
          d="M -0.62 -2.72 C 0.2 -3.42 1.48 -3.52 2.72 -2.68 C 3.92 -1.9 4.78 -1.02 5.02 0 C 4.78 1.02 3.92 1.9 2.72 2.68 C 1.48 3.52 0.2 3.42 -0.62 2.72"
        />
        <path
          className="snl-snake-head-dorsal"
          d="M -2.9 -0.66 C -1.75 -0.72 -0.72 -0.48 0.2 0 C -0.72 0.48 -1.75 0.72 -2.9 0.66 Z"
        />
        <path className="snl-snake-head-highlight" d="M -0.5 -1.3 Q 1.05 -2.92 2.75 -2.18" />
        <path
          className="snl-snake-head-plate crown"
          d="M 0.15 0 L 0.85 -0.78 L 1.62 0 L 0.85 0.78 Z"
        />
        <path
          className="snl-snake-head-plate snout"
          d="M 2.55 0 L 3.35 -0.6 L 4.15 0 L 3.35 0.6 Z"
        />
        <ellipse className="snl-snake-iris" cx="1.65" cy="-1.62" rx="0.58" ry="0.4" />
        <ellipse className="snl-snake-iris" cx="1.65" cy="1.62" rx="0.58" ry="0.4" />
        <ellipse className="snl-snake-pupil" cx="1.8" cy="-1.62" rx="0.1" ry="0.34" />
        <ellipse className="snl-snake-pupil" cx="1.8" cy="1.62" rx="0.1" ry="0.34" />
        <circle className="snl-snake-eye-glint" cx="1.52" cy="-1.75" r="0.08" />
        <circle className="snl-snake-eye-glint" cx="1.52" cy="1.49" r="0.08" />
        <path
          className="snl-snake-jaw upper"
          d="M 0.2 0 Q 0.7 -2.3 2.85 -2.58 Q 4.15 -1.72 4.96 0 Q 2.8 -0.38 0.2 0 Z"
        />
        <path
          className="snl-snake-jaw lower"
          d="M 0.2 0 Q 0.7 2.3 2.85 2.58 Q 4.15 1.72 4.96 0 Q 2.8 0.38 0.2 0 Z"
        />
        <path className="snl-snake-snout" d="M 3.72 -0.68 Q 4.75 0 3.72 0.68" />
        <circle className="snl-snake-nostril" cx="4.25" cy="-0.34" r="0.12" />
        <circle className="snl-snake-nostril" cx="4.25" cy="0.34" r="0.12" />
        <path className="snl-snake-mouth-side top" d="M 2.9 -1.66 Q 4.12 -1.28 4.86 -0.48" />
        <path className="snl-snake-mouth-side bottom" d="M 2.9 1.66 Q 4.12 1.28 4.86 0.48" />
        <path className="snl-snake-fang top" d="M 3.65 -0.14 l -0.38 0.94 l 0.78 -0.68 Z" />
        <path className="snl-snake-fang bottom" d="M 3.65 0.14 l -0.38 -0.94 l 0.78 0.68 Z" />
        <path
          className="snl-snake-tongue"
          d="M 4.88 0 L 6.65 0 M 6.55 0 l 0.78 -0.54 M 6.55 0 l 0.78 0.54"
        />
        <g className="snl-hiss-lines">
          <path d="M 6 -1.45 q 1.1 -0.65 1.8 0" />
          <path d="M 6.7 -2.35 q 1.2 -0.7 2 0.1" />
          <path d="M 6.7 1.75 q 1.05 0.7 1.8 0" />
        </g>
      </g>
    </g>
  );
}

function PhysicalDie({
  value,
  rolling,
  rollNonce,
  color,
  force,
}: {
  value: number | null;
  rolling: boolean;
  rollNonce: number;
  color: string;
  force: number;
}) {
  const faceValue = value ?? 1;
  const tumbleSeed = (rollNonce * 37 + faceValue * 19) % 7;
  const direction = tumbleSeed % 2 === 0 ? 1 : -1;
  const restingOrientation: Record<number, { x: number; y: number }> = {
    1: { x: -8, y: 10 },
    2: { x: -98, y: 7 },
    3: { x: -7, y: -80 },
    4: { x: -7, y: 100 },
    5: { x: 82, y: -7 },
    6: { x: 8, y: 190 },
  };
  const resting = restingOrientation[faceValue] ?? { x: -8, y: 10 };
  const turns = Math.round(clampLudoPhysics(force, 0, 1) * 2);
  const rollX = resting.x + (2 + (tumbleSeed % 2) + turns) * 360;
  const rollY = resting.y + (2 + ((tumbleSeed + 1) % 2) + turns) * 360;
  const rollZ = direction * (1 + (tumbleSeed % 2) + Math.ceil(turns / 2)) * 360;
  const style = {
    "--snl-player-color": color,
    "--snl-roll-x-flight": `${rollX * 0.24}deg`,
    "--snl-roll-y-flight": `${rollY * 0.22}deg`,
    "--snl-roll-z-flight": `${rollZ * 0.28}deg`,
    "--snl-roll-x-impact": `${rollX * 0.63}deg`,
    "--snl-roll-y-impact": `${rollY * 0.61}deg`,
    "--snl-roll-z-impact": `${rollZ * 0.66}deg`,
    "--snl-roll-x-bounce": `${rollX * 0.8}deg`,
    "--snl-roll-y-bounce": `${rollY * 0.78}deg`,
    "--snl-roll-z-bounce": `${rollZ * 0.82}deg`,
    "--snl-roll-x": `${rollX}deg`,
    "--snl-roll-y": `${rollY}deg`,
    "--snl-roll-z": `${rollZ}deg`,
  } as CSSProperties;
  const faces = [
    { value: 1, side: "front" },
    { value: 6, side: "back" },
    { value: 3, side: "right" },
    { value: 4, side: "left" },
    { value: 2, side: "top" },
    { value: 5, side: "bottom" },
  ];

  return (
    <>
      <span
        className={`snl-die-shell ${rolling ? "rolling" : ""}`}
        style={style}
        aria-hidden="true"
      >
        <span className={`snl-die-model value-${faceValue} ${rolling ? "rolling" : ""}`}>
          {faces.map((face) => (
            <span className={`snl-die-face ${face.side}`} key={face.side}>
              {dicePips[face.value]?.map((pip) => (
                <i
                  style={{ left: `${pip.left}%`, top: `${pip.top}%` }}
                  key={`${face.value}:${pip.left}:${pip.top}`}
                />
              ))}
            </span>
          ))}
        </span>
      </span>
      <span className="snl-dice-shadow" aria-hidden="true" />
    </>
  );
}

export function SnakesLaddersBoard({
  state,
  participantId,
  participants,
  pending,
  isHost,
  settingsPending,
  serverError,
  playAgain,
  onRoll,
  onFastModeChange,
  onMovePresentationComplete,
}: SnakesLaddersBoardProps) {
  const [displayPositions, setDisplayPositions] = useState<Record<string, number>>(() =>
    Object.fromEntries(state.players.map((player) => [player.id, player.position])),
  );
  const [movingPlayerId, setMovingPlayerId] = useState<string | null>(null);
  const [transport, setTransport] = useState<"snake" | "ladder" | null>(null);
  const [bitingSnakeFrom, setBitingSnakeFrom] = useState<number | null>(null);
  const [diceRolling, setDiceRolling] = useState(false);
  const [diceLanded, setDiceLanded] = useState(false);
  const [displayedRoll, setDisplayedRoll] = useState<number | null>(null);
  const [diceAnimationKey, setDiceAnimationKey] = useState(0);
  const [minimumRollComplete, setMinimumRollComplete] = useState(true);
  const [timerNow, setTimerNow] = useState(Date.now());
  const [diceDrag, setDiceDrag] = useState({ x: 0, y: 0, active: false });
  const [throwPhysics, setThrowPhysics] = useState<LudoThrowPhysics>(defaultLudoThrowPhysics);
  const animatedNonce = useRef(0);
  const lastMoveRef = useRef(state.lastMove);
  const presentationCompleteRef = useRef(onMovePresentationComplete);
  const movingPlayerIdRef = useRef<string | null>(null);
  const observedRollNonce = useRef(state.moveNonce);
  const rollStartNonce = useRef(state.moveNonce);
  const rollOwnerId = useRef(participantId);
  const diceLandTimer = useRef<number | null>(null);
  const rollEndTimer = useRef<number | null>(null);
  const rollPresentationUntil = useRef(0);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const diceButtonRef = useRef<HTMLButtonElement | null>(null);
  const suppressDiceClick = useRef(false);
  const diceDragState = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    lastAt: number;
    velocityX: number;
    velocityY: number;
    maxDrag: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } | null>(null);
  const lastMoveNonce = state.lastMove?.nonce;
  lastMoveRef.current = state.lastMove;
  presentationCompleteRef.current = onMovePresentationComplete;

  const activePlayer = state.players[state.turnIndex];
  const humanTurn = state.phase === "playing" && activePlayer?.id === participantId;
  const canRoll = humanTurn && !pending && !diceRolling && !movingPlayerId;
  const moveAwaitingPresentation = Boolean(
    lastMoveNonce !== undefined && lastMoveNonce > animatedNonce.current,
  );
  const presentingMove = Boolean(movingPlayerId || moveAwaitingPresentation);
  const showFinishedState = state.phase === "finished" && !presentingMove;
  const showPlayingSurface = state.phase === "playing" || presentingMove;
  const turnTimeRemainingMs =
    state.phase !== "playing" || state.turnDeadlineAt === null
      ? 0
      : Math.min(state.rollTimeSeconds * 1_000, Math.max(0, state.turnDeadlineAt - timerNow));
  const turnTimeRemaining = Math.ceil(turnTimeRemainingMs / 1_000);
  const turnTimeProgress =
    (turnTimeRemainingMs /
      Math.max(1, state.rollTimeSeconds * 1_000 || SNAKES_LADDERS_ROLL_TIME_SECONDS * 1_000)) *
    100;
  const turnTimerUrgency =
    turnTimeRemaining <= 5 ? "ending" : turnTimeRemaining <= 10 ? "warning" : "";
  const diceResultHoldMs = state.fastMode ? FAST_DICE_RESULT_HOLD_MS : NORMAL_DICE_RESULT_HOLD_MS;
  const tokenStepMs = state.fastMode ? FAST_TOKEN_STEP_MS : NORMAL_TOKEN_STEP_MS;
  const latestRoll = state.lastMove?.roll ?? null;
  const visualRollerId = diceRolling ? rollOwnerId.current : activePlayer?.id;
  const visualRollerIndex = Math.max(
    0,
    state.players.findIndex((player) => player.id === visualRollerId),
  );
  const visualRoller = state.players[visualRollerIndex];
  const visualRollerName =
    visualRoller?.id === participantId
      ? "You"
      : (participants.find((person) => person.id === visualRoller?.id)?.displayName ??
        (visualRoller?.id.startsWith("ai:") ? "Topzle Bot" : "Player"));
  const visualRollerColor = visualRoller?.color ?? playerColors[visualRollerIndex] ?? "blue";
  const diceColor = playerColorHex[visualRollerColor];
  const transitions = useMemo(() => Object.entries(snakesLaddersTransitions), []);

  const startDiceAnimation = useCallback(
    (physics: LudoThrowPhysics, ownerId: string, startNonce: number) => {
      const requestedPhysics = state.fastMode
        ? { ...physics, durationMs: Math.max(520, Math.round(physics.durationMs * 0.48)) }
        : physics;
      const boardRect = boardRef.current?.getBoundingClientRect();
      const dieRect = diceButtonRef.current?.getBoundingClientRect();
      const bounded =
        boardRect && dieRect
          ? constrainLudoThrowToBoard(
              requestedPhysics,
              boardRect.width,
              boardRect.height,
              dieRect.width,
              dieRect.height,
            )
          : requestedPhysics;
      setThrowPhysics(bounded);
      setDiceDrag({ x: 0, y: 0, active: false });
      rollOwnerId.current = ownerId;
      rollStartNonce.current = startNonce;
      rollPresentationUntil.current = Date.now() + bounded.durationMs + diceResultHoldMs;
      setMinimumRollComplete(false);
      setDiceLanded(false);
      setDisplayedRoll(null);
      setDiceAnimationKey((animationKey) => animationKey + 1);
      setDiceRolling(true);
      if (diceLandTimer.current !== null) window.clearTimeout(diceLandTimer.current);
      if (rollEndTimer.current !== null) window.clearTimeout(rollEndTimer.current);
      diceLandTimer.current = window.setTimeout(() => {
        setDiceLanded(true);
        diceLandTimer.current = null;
      }, bounded.durationMs);
      rollEndTimer.current = window.setTimeout(() => {
        setMinimumRollComplete(true);
        rollEndTimer.current = null;
      }, bounded.durationMs + diceResultHoldMs);
      return bounded;
    },
    [diceResultHoldMs, state.fastMode],
  );

  useEffect(
    () => () => {
      if (diceLandTimer.current !== null) window.clearTimeout(diceLandTimer.current);
      if (rollEndTimer.current !== null) window.clearTimeout(rollEndTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (state.phase !== "playing" || state.turnDeadlineAt === null) return;
    setTimerNow(Date.now());
    const timer = window.setInterval(() => setTimerNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, [state.phase, state.turnDeadlineAt]);

  useEffect(() => {
    if (!diceRolling) return;
    if (serverError || (minimumRollComplete && state.moveNonce !== rollStartNonce.current)) {
      setDiceRolling(false);
      setDiceLanded(false);
    }
  }, [diceRolling, minimumRollComplete, serverError, state.moveNonce]);

  useEffect(() => {
    if (state.moveNonce === observedRollNonce.current || !state.lastMove) return;
    const previousNonce = observedRollNonce.current;
    observedRollNonce.current = state.moveNonce;
    const alreadyRollingThisThrow =
      diceRolling &&
      rollOwnerId.current === state.lastMove.playerId &&
      rollStartNonce.current === previousNonce;
    if (!alreadyRollingThisThrow) {
      const force = 0.48 + ((state.moveNonce * 17) % 35) / 100;
      startDiceAnimation(
        {
          ...defaultLudoThrowPhysics,
          durationMs: Math.round(1_850 - force * 620),
          force,
          momentumX: ((state.moveNonce % 3) - 1) * 34,
          momentumY: (((state.moveNonce + 1) % 3) - 1) * 28,
          lateMomentumX: ((state.moveNonce % 3) - 1) * 8,
          lateMomentumY: (((state.moveNonce + 1) % 3) - 1) * 7,
          reboundX: ((state.moveNonce % 3) - 1) * -7,
          reboundY: (((state.moveNonce + 1) % 3) - 1) * -6,
          pathTilt: ((state.moveNonce % 5) - 2) * 4,
        },
        state.lastMove.playerId,
        previousNonce,
      );
    }
    setDisplayedRoll(state.lastMove.roll);
    const landingAt = rollPresentationUntil.current - diceResultHoldMs;
    const landingDelay = Math.max(0, landingAt - Date.now());
    const presentationEnd = Math.max(landingAt, Date.now()) + diceResultHoldMs;
    rollPresentationUntil.current = presentationEnd;
    setMinimumRollComplete(false);
    if (diceLandTimer.current !== null) window.clearTimeout(diceLandTimer.current);
    if (rollEndTimer.current !== null) window.clearTimeout(rollEndTimer.current);
    diceLandTimer.current = window.setTimeout(() => {
      setDiceLanded(true);
      diceLandTimer.current = null;
    }, landingDelay);
    rollEndTimer.current = window.setTimeout(
      () => {
        setMinimumRollComplete(true);
        rollEndTimer.current = null;
      },
      Math.max(0, presentationEnd - Date.now()),
    );
  }, [diceResultHoldMs, diceRolling, startDiceAnimation, state.lastMove, state.moveNonce]);

  useEffect(() => {
    setDisplayPositions((current) => {
      const next = { ...current };
      for (const player of state.players) {
        if (player.id !== movingPlayerIdRef.current) next[player.id] = player.position;
      }
      return next;
    });
  }, [state.players]);

  useEffect(() => {
    if (lastMoveNonce === undefined) return;
    const move = lastMoveRef.current;
    if (!move || move.nonce !== lastMoveNonce || move.nonce <= animatedNonce.current) return;
    animatedNonce.current = move.nonce;
    const timers: number[] = [];
    const diceDelay = Math.max(0, rollPresentationUntil.current - Date.now());
    movingPlayerIdRef.current = move.playerId;
    setMovingPlayerId(move.playerId);
    setTransport(null);
    setBitingSnakeFrom(null);
    setDisplayPositions((positions) => ({ ...positions, [move.playerId]: move.from }));
    const steps = Math.max(0, move.landedAt - move.from);
    for (let step = 1; step <= steps; step += 1) {
      timers.push(
        window.setTimeout(
          () => {
            setDisplayPositions((positions) => ({
              ...positions,
              [move.playerId]: move.from + step,
            }));
          },
          diceDelay + step * tokenStepMs,
        ),
      );
    }
    const transportDelay = diceDelay + steps * tokenStepMs + (state.fastMode ? 80 : 180);
    if (move.transport === "snake") {
      timers.push(window.setTimeout(() => setBitingSnakeFrom(move.landedAt), transportDelay));
      timers.push(
        window.setTimeout(() => {
          setTransport("snake");
          setDisplayPositions((positions) => ({ ...positions, [move.playerId]: move.to }));
        }, transportDelay + 620),
      );
    } else if (move.transport === "ladder") {
      timers.push(
        window.setTimeout(() => {
          setTransport("ladder");
          setDisplayPositions((positions) => ({ ...positions, [move.playerId]: move.to }));
        }, transportDelay),
      );
    }
    timers.push(
      window.setTimeout(
        () => {
          if (movingPlayerIdRef.current === move.playerId) movingPlayerIdRef.current = null;
          setMovingPlayerId(null);
          setTransport(null);
          setBitingSnakeFrom(null);
          presentationCompleteRef.current?.(move.nonce);
        },
        transportDelay +
          (move.transport === "snake"
            ? state.fastMode
              ? 680
              : 1_520
            : move.transport === "ladder"
              ? state.fastMode
                ? 350
                : 780
              : state.fastMode
                ? 90
                : 180),
      ),
    );
    return () => {
      timers.forEach(window.clearTimeout);
      if (movingPlayerIdRef.current === move.playerId) {
        movingPlayerIdRef.current = null;
        setMovingPlayerId(null);
      }
    };
  }, [lastMoveNonce, state.fastMode, tokenStepMs]);

  useEffect(() => {
    if (humanTurn) return;
    diceDragState.current = null;
    setDiceDrag((value) =>
      value.active || value.x || value.y ? { x: 0, y: 0, active: false } : value,
    );
  }, [humanTurn]);

  const rollDice = (physics: LudoThrowPhysics = defaultLudoThrowPhysics) => {
    if (!canRoll) return;
    startDiceAnimation(physics, participantId, state.moveNonce);
    if (!onRoll()) {
      setDiceRolling(false);
      setMinimumRollComplete(true);
      if (rollEndTimer.current !== null) window.clearTimeout(rollEndTimer.current);
    }
  };

  const beginDiceDrag = (event: PointerEvent<HTMLButtonElement>) => {
    if (!canRoll) return;
    event.preventDefault();
    const boardRect = boardRef.current?.getBoundingClientRect();
    const dieRect = event.currentTarget.getBoundingClientRect();
    const boardWidth = boardRect?.width ?? 420;
    const inset = Math.max(5, boardWidth * 0.015);
    const envelopeX = dieRect.width * 0.72;
    const envelopeY = dieRect.height * 0.72;
    const minX = boardRect
      ? boardRect.left + inset + envelopeX - (dieRect.left + dieRect.width / 2)
      : -boardWidth * 0.4;
    const maxX = boardRect
      ? boardRect.right - inset - envelopeX - (dieRect.left + dieRect.width / 2)
      : boardWidth * 0.4;
    const minY = boardRect
      ? boardRect.top + inset + envelopeY - (dieRect.top + dieRect.height / 2)
      : -boardWidth * 0.4;
    const maxY = boardRect
      ? boardRect.bottom - inset - envelopeY - (dieRect.top + dieRect.height / 2)
      : boardWidth * 0.4;
    const maxDrag = Math.max(90, Math.abs(minX), Math.abs(maxX), Math.abs(minY), Math.abs(maxY));
    event.currentTarget.setPointerCapture(event.pointerId);
    diceDragState.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      lastAt: event.timeStamp,
      velocityX: 0,
      velocityY: 0,
      maxDrag,
      minX,
      maxX,
      minY,
      maxY,
    };
    setDiceDrag({ x: 0, y: 0, active: true });
  };

  const completeDiceDrag = (
    event: PointerEvent<HTMLButtonElement>,
    edgeRelease?: { x: number; y: number; velocityX: number; velocityY: number },
  ) => {
    const drag = diceDragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    diceDragState.current = null;
    const x = edgeRelease?.x ?? clampLudoPhysics(event.clientX - drag.startX, drag.minX, drag.maxX);
    const y = edgeRelease?.y ?? clampLudoPhysics(event.clientY - drag.startY, drag.minY, drag.maxY);
    suppressDiceClick.current = true;
    window.setTimeout(() => {
      suppressDiceClick.current = false;
    }, 400);
    if (Math.hypot(x, y) < 7) {
      rollDice();
      return;
    }
    const elapsed = Math.max(8, event.timeStamp - drag.lastAt);
    const velocityX =
      edgeRelease?.velocityX ??
      drag.velocityX * 0.55 + ((event.clientX - drag.lastX) / elapsed) * 0.45;
    const velocityY =
      edgeRelease?.velocityY ??
      drag.velocityY * 0.55 + ((event.clientY - drag.lastY) / elapsed) * 0.45;
    rollDice(
      calculateLudoThrowPhysics({
        distanceX: x,
        distanceY: y,
        maxDrag: drag.maxDrag,
        velocityX,
        velocityY,
      }),
    );
  };

  const moveDiceDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = diceDragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const elapsed = Math.max(8, event.timeStamp - drag.lastAt);
    const instantX = (event.clientX - drag.lastX) / elapsed;
    const instantY = (event.clientY - drag.lastY) / elapsed;
    const velocityX = drag.velocityX * 0.35 + instantX * 0.65;
    const velocityY = drag.velocityY * 0.35 + instantY * 0.65;
    const distanceX = event.clientX - drag.startX;
    const distanceY = event.clientY - drag.startY;
    const x = clampLudoPhysics(distanceX, drag.minX, drag.maxX);
    const y = clampLudoPhysics(distanceY, drag.minY, drag.maxY);
    setDiceDrag({ x, y, active: true });
    if (hasLudoDiceTouchedEdge(distanceX, distanceY, drag)) {
      completeDiceDrag(event, { x, y, velocityX, velocityY });
      return;
    }
    drag.velocityX = velocityX;
    drag.velocityY = velocityY;
    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    drag.lastAt = event.timeStamp;
  };

  const cancelDiceDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = diceDragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (Math.hypot(drag.lastX - drag.startX, drag.lastY - drag.startY) >= 7) {
      completeDiceDrag(event, {
        x: clampLudoPhysics(drag.lastX - drag.startX, drag.minX, drag.maxX),
        y: clampLudoPhysics(drag.lastY - drag.startY, drag.minY, drag.maxY),
        velocityX: drag.velocityX,
        velocityY: drag.velocityY,
      });
      return;
    }
    diceDragState.current = null;
    setDiceDrag({ x: 0, y: 0, active: false });
  };

  const diceStyle = {
    "--snl-player-color": diceColor,
    "--snl-dice-drag-x": `${diceDrag.x}px`,
    "--snl-dice-drag-y": `${diceDrag.y}px`,
    "--snl-dice-start-x": `${throwPhysics.startX}px`,
    "--snl-dice-start-y": `${throwPhysics.startY}px`,
    "--snl-dice-momentum-x": `${throwPhysics.momentumX}px`,
    "--snl-dice-momentum-y": `${throwPhysics.momentumY}px`,
    "--snl-dice-wall-1-x": `${throwPhysics.wall1X}px`,
    "--snl-dice-wall-1-y": `${throwPhysics.wall1Y}px`,
    "--snl-dice-wall-2-x": `${throwPhysics.wall2X}px`,
    "--snl-dice-wall-2-y": `${throwPhysics.wall2Y}px`,
    "--snl-dice-wall-3-x": `${throwPhysics.wall3X}px`,
    "--snl-dice-wall-3-y": `${throwPhysics.wall3Y}px`,
    "--snl-dice-duration": `${throwPhysics.durationMs}ms`,
    "--snl-dice-presentation-duration": `${throwPhysics.durationMs + diceResultHoldMs}ms`,
    "--snl-dice-lift": `${74 + throwPhysics.force * 86}px`,
    "--snl-dice-path-tilt": `${throwPhysics.pathTilt}deg`,
  } as CSSProperties;

  return (
    <section className="snl-game" aria-label="Snakes and Ladders game">
      <div className="snl-status">
        <div>
          <span className="eyebrow">SNAKES &amp; LADDERS</span>
          <strong>
            {showFinishedState
              ? `${participants.find((person) => person.id === state.winnerId)?.displayName ?? "A player"} reached 100!`
              : state.phase === "finished" && state.lastMove
                ? `${participants.find((person) => person.id === state.lastMove?.playerId)?.displayName ?? (state.lastMove.playerId.startsWith("ai:") ? "Topzle Bot" : "Player")} is making the final move`
                : activePlayer?.id === participantId
                  ? "Your turn — drag or tap the dice"
                  : `${participants.find((person) => person.id === activePlayer?.id)?.displayName ?? "Player"} is rolling`}
          </strong>
        </div>
        <div className="snl-status-actions">
          <button
            type="button"
            className={state.fastMode ? "snl-fast-toggle active" : "snl-fast-toggle"}
            aria-pressed={state.fastMode}
            aria-label={`${state.fastMode ? "Disable" : "Enable"} fast Snakes and Ladders animations${isHost ? "" : ". Only the host can change this setting"}`}
            disabled={!isHost || settingsPending}
            onClick={() => onFastModeChange(!state.fastMode)}
          >
            <Gauge />
            <span>Fast</span>
            <i aria-hidden="true" />
          </button>
          <div className="snl-roll-state" role="status" aria-live="polite">
            {diceRolling || pending ? <LoaderCircle className="spin" /> : null}
            <span>
              {bitingSnakeFrom
                ? "HSSS — snake bite!"
                : diceRolling
                  ? diceLanded && displayedRoll
                    ? `${visualRollerName} rolled ${displayedRoll}`
                    : `${visualRollerName} ${visualRollerName === "You" ? "are" : "is"} throwing…`
                  : state.lastMove
                    ? `${state.lastMove.roll} · ${state.lastMove.transport === "snake" ? "bitten" : state.lastMove.transport === "ladder" ? "climbed" : "last roll"}`
                    : "Drag or tap to roll"}
            </span>
          </div>
        </div>
      </div>

      {state.phase === "playing" && state.turnDeadlineAt !== null && (
        <div
          className={`snl-action-timer ${turnTimerUrgency}`}
          role="timer"
          aria-label={`${turnTimeRemaining} seconds remaining to roll the dice`}
        >
          <i style={{ width: `${turnTimeProgress}%` }} aria-hidden="true" />
          <span>
            {turnTimeRemaining > 0
              ? `${turnTimeRemaining}s to roll`
              : `Time’s up · Topzle is rolling for ${activePlayer?.id === participantId ? "you" : (participants.find((person) => person.id === activePlayer?.id)?.displayName ?? "the player")}`}
          </span>
        </div>
      )}

      <div className="snl-board-wrap">
        <div className="snl-board" ref={boardRef}>
          {boardSquares.map((square) => (
            <div
              className={`snl-cell ${square === 100 ? "finish" : ""} ${square === 1 ? "start" : ""}`}
              key={square}
            >
              <span>{square}</span>
            </div>
          ))}
          <svg className="snl-routes" viewBox="0 0 100 100" aria-hidden="true">
            {transitions.map(([fromValue, toValue], index) => {
              const from = Number(fromValue);
              const to = Number(toValue);
              return to > from ? (
                <Ladder from={from} to={to} key={from} />
              ) : (
                <Snake
                  from={from}
                  to={to}
                  index={index}
                  biting={bitingSnakeFrom === from}
                  key={from}
                />
              );
            })}
          </svg>
          {state.players.map((player, index) => {
            const position = displayPositions[player.id] ?? player.position;
            const point = squarePoint(Math.max(1, position));
            const offset = state.players
              .filter((item) => (displayPositions[item.id] ?? item.position) === position)
              .findIndex((item) => item.id === player.id);
            const name =
              participants.find((person) => person.id === player.id)?.displayName ??
              (player.id.startsWith("ai:") ? "Topzle Bot" : "Player");
            const beingBitten = movingPlayerId === player.id && bitingSnakeFrom === position;
            const local = player.id === participantId;
            const playerColor = player.color ?? playerColors[index] ?? "blue";
            const placementIndex = (state.placementIds ?? []).indexOf(player.id);
            return (
              <div
                className={`snl-token ${playerColor} ${local ? "you" : ""} ${movingPlayerId === player.id ? "moving" : ""} ${beingBitten ? "bitten" : ""} ${movingPlayerId === player.id && transport ? transport : ""} ${placementIndex >= 0 ? "winner" : ""}`}
                style={{
                  left: `${point.x + (offset % 2) * 2 - 1}%`,
                  top: `${point.y + Math.floor(offset / 2) * 2 - 1}%`,
                }}
                title={`${name}: square ${player.position || "start"}`}
                key={player.id}
              >
                <span>{local ? "You" : index + 1}</span>
                <small>{name}</small>
                {placementIndex >= 0 && (
                  <b className="snl-placement-badge">
                    🏆 {placementIndex === 0 ? "1st" : `#${placementIndex + 1}`}
                  </b>
                )}
              </div>
            );
          })}
          {showPlayingSurface && (
            <button
              key={diceAnimationKey}
              ref={diceButtonRef}
              type="button"
              className={`snl-physical-dice ${diceRolling ? "rolling" : ""} ${diceLanded ? "landed" : ""} ${diceDrag.active ? "dragging" : ""} bounces-${throwPhysics.bounceCount}`}
              style={diceStyle}
              disabled={!canRoll}
              onPointerDown={beginDiceDrag}
              onPointerMove={moveDiceDrag}
              onPointerUp={completeDiceDrag}
              onPointerCancel={cancelDiceDrag}
              onContextMenu={(event) => event.preventDefault()}
              onDragStart={(event) => event.preventDefault()}
              onClick={() => {
                if (suppressDiceClick.current) {
                  suppressDiceClick.current = false;
                  return;
                }
                rollDice();
              }}
              aria-label={
                canRoll
                  ? "Drag and throw the dice, or tap to roll"
                  : latestRoll
                    ? `Latest roll was ${latestRoll}`
                    : "Wait for your turn"
              }
              title={canRoll ? "Drag to throw · tap to roll" : undefined}
            >
              <PhysicalDie
                value={diceRolling ? displayedRoll : latestRoll}
                rolling={diceRolling}
                rollNonce={
                  diceRolling && state.moveNonce === rollStartNonce.current
                    ? state.moveNonce + 1
                    : state.moveNonce
                }
                color={diceColor}
                force={throwPhysics.force}
              />
              {diceLanded && displayedRoll && (
                <span className="snl-dice-landed-face" aria-hidden="true">
                  {dicePips[displayedRoll]?.map((pip) => (
                    <i
                      style={{ left: `${pip.left}%`, top: `${pip.top}%` }}
                      key={`landed:${displayedRoll}:${pip.left}:${pip.top}`}
                    />
                  ))}
                </span>
              )}
            </button>
          )}
          {showPlayingSurface && diceRolling && (
            <div
              key={`roller-${diceAnimationKey}`}
              className="snl-dice-owner"
              style={{ "--snl-player-color": diceColor } as CSSProperties}
              aria-hidden="true"
            >
              <i />
              <span>
                {diceLanded && displayedRoll
                  ? `${visualRollerName} rolled ${displayedRoll}`
                  : `${visualRollerName} ${visualRollerName === "You" ? "are" : "is"} throwing`}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="snl-controls">
        {!showFinishedState ? (
          <small>
            {state.phase === "finished"
              ? "Finishing the final move…"
              : canRoll
                ? "Drag the 3D dice across the board and release, or tap it."
                : diceRolling || pending
                  ? "Everyone sees this throw before the goti moves."
                  : "Waiting for the current player."}
          </small>
        ) : (
          playAgain
        )}
        {serverError && <p className="inline-error">{serverError}</p>}
      </div>
    </section>
  );
}
